// filepath: d:\COMP9900\SDG-Hub-main\frontend\src\pages\Chat\components\ReferenceCards.tsx
import React, { useState } from 'react';
import { References } from '../types';
import { MdLink, MdPublic, MdInfoOutline, MdExpandMore, MdExpandLess } from 'react-icons/md';

interface ReferenceCardsProps {
  references?: References;
}

const ReferenceCards: React.FC<ReferenceCardsProps> = ({ references }) => {
  const [expandedInternal, setExpandedInternal] = useState<Set<number>>(new Set());
  const [expandedWeb, setExpandedWeb] = useState<Set<number>>(new Set());

  if (!references) return null;

  const hasInternalReferences = references.internal_references && references.internal_references.length > 0;
  const hasWebReferences = references.web_references && references.web_references.length > 0;

  if (!hasInternalReferences && !hasWebReferences) return null;

  const toggleInternalExpand = (index: number) => {
    const newExpanded = new Set(expandedInternal);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedInternal(newExpanded);
  };

  const toggleWebExpand = (index: number) => {
    const newExpanded = new Set(expandedWeb);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedWeb(newExpanded);
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Internal References */}
      {hasInternalReferences && (
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <div className="flex items-center gap-2 mb-3">
            <MdLink className="w-5 h-5 text-blue-600" />
            <h4 className="text-sm font-semibold text-blue-900">Internal References</h4>
            <div className="relative inline-block">
              <MdInfoOutline className="w-4 h-4 text-blue-500 cursor-help peer" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10 pointer-events-none opacity-0 peer-hover:opacity-100 transition-opacity duration-300">
                Internal Reference refers to entries retrieved from the SDG database.
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {references.internal_references!.map((ref, index) => {
              const isExpanded = expandedInternal.has(index);
              return (
                <div
                  key={index}
                  className="bg-white border border-blue-200 rounded-md overflow-hidden"
                >
                  <button
                    onClick={() => toggleInternalExpand(index)}
                    className="w-full p-3 flex items-center justify-between hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="text-sm font-medium text-blue-700 text-left flex-1 truncate">
                        {ref.title}
                      </div>
                      {/*<span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap border border-blue-300">*/}
                      {/*  {(ref.score * 100).toFixed(0)}%*/}
                      {/*</span>*/}
                    </div>
                    {isExpanded ? (
                      <MdExpandLess className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                    ) : (
                      <MdExpandMore className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      <a
                        href={ref.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline break-all inline-block"
                      >
                        Click Here to Source: {ref.title}
                      </a>
                      <blockquote className="border-l-4 border-blue-400 pl-3 py-2 bg-blue-50 text-sm text-gray-700 italic">
                        {ref.excerpt}
                      </blockquote>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Web References */}
      {hasWebReferences && (
        <div className="border border-green-200 rounded-lg p-4 bg-green-50">
          <div className="flex items-center gap-2 mb-3">
            <MdPublic className="w-5 h-5 text-green-600" />
            <h4 className="text-sm font-semibold text-green-900">Web References</h4>
            <div className="relative inline-block">
              <MdInfoOutline className="w-4 h-4 text-green-500 cursor-help peer" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10 pointer-events-none opacity-0 peer-hover:opacity-100 transition-opacity duration-300">
                Web Reference refers to related information obtained from the internet. Please pay attention to identifying the source and reliability of the information.
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {references.web_references!.map((ref, index) => {
              const isExpanded = expandedWeb.has(index);
              return (
                <div
                  key={index}
                  className="bg-white border border-green-200 rounded-md overflow-hidden"
                >
                  <button
                    onClick={() => toggleWebExpand(index)}
                    className="w-full p-3 flex items-center justify-between hover:bg-green-50 transition-colors"
                  >
                    <div className="text-sm font-medium text-green-700 text-left">
                      {ref.title}
                    </div>
                    {isExpanded ? (
                      <MdExpandLess className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                    ) : (
                      <MdExpandMore className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:underline break-all inline-block"
                      >
                        Click Here to Source: {ref.title}
                      </a>
                      <blockquote className="border-l-4 border-green-400 pl-3 py-2 bg-green-50 text-sm text-gray-700 italic">
                        {ref.snippet}
                      </blockquote>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenceCards;

